import { pick } from "lodash";

import type {
  ExploreField,
  ExploreFieldOverrides,
  ExploreSpec,
} from "./types.js";
import { ExploreFieldOverride } from "./types.js";

export interface ExploreSpecSessionStateUpdateableFields {
  fieldOverrides: ExploreFieldOverrides | null;
}

/**
 * The keys of an `ExploreFieldOverride` that may be applied on top of a base
 * `ExploreField`, derived from the runtype. This acts as an allowlist:
 * `ExploreSpecSessionState` is client-controlled and persisted as untyped
 * JSON, so we must never spread arbitrary keys onto the field (which drives
 * query generation).
 */
const EXPLORE_FIELD_OVERRIDE_SAFE_KEYS = Object.keys(
  ExploreFieldOverride.fields,
) as (keyof ExploreFieldOverride)[];

/**
 * Merges an ExploreFieldOverride with the base ExploreField.
 *
 * @param field - The base ExploreField from the ExploreSpec
 * @param fieldOverride - The override from ExploreSpecSessionState
 * @returns The merged ExploreField with overrides applied
 */
export function mergeExploreFieldWithOverride(
  field: ExploreField,
  fieldOverride: ExploreFieldOverride | undefined,
): ExploreField {
  if (!fieldOverride) {
    return field;
  }

  const overrides = Object.fromEntries(
    Object.entries(
      pick(fieldOverride, EXPLORE_FIELD_OVERRIDE_SAFE_KEYS),
    ).filter(([_, value]) => value != null),
  );

  return {
    ...field,
    ...overrides,
  };
}

/**
 * Merges ExploreSpecSessionState with the base ExploreSpec.
 *
 * @param spec - The base ExploreSpec
 * @param sessionState - The session state with field overrides
 * @returns The merged ExploreSpec with session state overrides applied
 */
export function mergeExploreSpecWithSessionState(
  spec: ExploreSpec,
  sessionState: ExploreSpecSessionStateUpdateableFields | null,
): ExploreSpec {
  const fieldOverrides = sessionState?.fieldOverrides;
  if (fieldOverrides == null) {
    return spec;
  }

  const fields = spec.fields.map((field) => {
    const override = fieldOverrides[field.id];
    return mergeExploreFieldWithOverride(field, override);
  });

  return {
    ...spec,
    fields,
  };
}
